import Vue from 'vue'
import uuid from 'uuid/v4'
import Notification from './Notification'
import NotificationGroup from './NotificationGroup'
import logo from 'assets/logo.png'
import sound from 'assets/notice.mp3'

const PERMISSION_AGREE = 'granted'
const Notifier = window.Notification || window.webkitNotification
const defaultOptions = {
  title: '',
  content: '',
  silent: true,
  autoFocus: true,
  onOpen: function() {},
  onClose: function() {},
  onClick: function() {}
}
let instance = null
let nativeInstance = null

/**
 * 判断原生提醒权限
 * @returns {boolean}
 */
Notification.hasNativePermission = function() {
  return !!Notifier && Notifier.permission === PERMISSION_AGREE
}

/**
 * 打开通知消息
 * @param {boolean} options.useNative - 是否使用浏览器通知
 * @param {string} options.id - 通知的 ID 标识（必要的时候可用来进行刷新、替换、移除）
 * @param {string} options.title - 通知标题
 * @param {string} options.icon - 通知消息的图标
 * @param {string} options.content - 通知内容
 * @param {boolean} options.rtl - RTL模式
 * @param {function} options.onClick - 点击消息回调
 * @param {function} options.onError - 消息提醒报错
 * @param {function} options.onOpen - 弹出消息回调
 * @param {function} options.onClose - 关闭消息回调
 *
 * @returns {function} rtn.close - 关闭方法
 */
Notification.open = function(options) {
  const id = uuid('notification')
  const { useNative, ...params } = { ...defaultOptions, ...options }
  params.id = id

  // 提示音在打开之后提示
  if (!params.silent) {
    const onOpen = params.onOpen
    params.onOpen = function() {
      new Audio(sound).play()
      onOpen && onOpen()
    }
  }
  // 点击后自动聚焦到当前页面
  if (params.autoFocus) {
    const onClick = params.onClick
    params.onClick = function() {
      window.focus()
      onClick && onClick()
    }
  }
  if (useNative && Notifier) {
    if (Notification.hasNativePermission()) {
      showNativeNotification(params)
    } else {
      Notifier.requestPermission(function(permission) {
        // 如果用户拒绝权限，调用自定义提醒
        if (permission === PERMISSION_AGREE) {
          showNativeNotification(params)
        } else {
          showCustomNotification(params)
        }
      })
    }
  } else {
    showCustomNotification(params)
  }
  return id
}

/**
 * 手动关闭提醒
 * @param {number} id - 提醒 id，调用打开时返回值
 */
Notification.close = function(id) {
  if (nativeInstance) nativeInstance.close(id)
  if (instance) instance.remove(id)
}

/**
 * 原生消息提醒（全局系统提示，不在当前页面也可以看到）
 * @param data
 * @returns {Notification}
 */
function showNativeNotification(data) {
  const { title, content, id, rtl } = data
  const dir = rtl === true ? 'rtl' : rtl === false ? 'ltr' : 'auto'
  nativeInstance = new Notifier(title, {
    tag: id,
    icon: location.origin + logo,
    image: location.origin + logo,
    body: content,
    dir
  })
  nativeInstance.addEventListener('click', data.onClick)
  nativeInstance.addEventListener('error', data.onError)
  nativeInstance.addEventListener('show', data.onOpen)
  nativeInstance.addEventListener('close', data.onClose)
}

/**
 * 自定义提醒（当前页面可见）
 * @param params
 * @returns {{close(): void}}
 */
function showCustomNotification(params) {
  const notice = getNotificationInstance()
  notice.add(params)
  return {
    close() {
      notice.remove(params.id)
    }
  }
}

function getNotificationInstance() {
  if (instance) return instance
  const notification = new (Vue.extend(NotificationGroup))()
  notification.$mount()
  document.body.appendChild(notification.$el)
  instance = {
    add(props) {
      notification.add(props)
    },
    remove(uuid) {
      notification.remove(uuid)
    },
    instance: notification,
    destroy() {
      notification.removeAll()
      document.body.removeChild(notification.$el)
      instance = null
    }
  }
  return instance
}

export default Notification
